import { useEffect, useState } from "react";
import { io } from "socket.io-client";
import { Link } from "react-router-dom";
import { Loader2, MessageCircle } from "lucide-react";
import { listContas } from "../WhatsappOficial/services/whatsappOficialApi";
import InboxLayout from "../WhatsappOficial/components/inbox/InboxLayout";

// Página /chat: inbox Meta WhatsApp em modo fullscreen, sem o wrapping
// de tabs/conta-card que existe em /whatsapp/oficial. Pega automaticamente
// a primeira conta com status "ativo" (ou a primeira disponível).
export default function Chat() {
  const [contas, setContas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    listContas()
      .then((data) => setContas(Array.isArray(data) ? data : []))
      .catch(() => setContas([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // CRITICAL: io('https://metriqa.com.br/api') interpreta '/api' como
    // NAMESPACE — conecta em /socket.io/ (root) e ignora /api. Como nginx
    // só faz proxy_pass de /api/* pro backend, conexões em /socket.io/
    // batem no frontend (404). Solução: passa origem como URL e path
    // explícito '/api/socket.io/' (que casa com o backend e o nginx).
    // Cookie httpOnly chega automaticamente via withCredentials — não dá
    // pra ler com document.cookie aqui (fallback antigo removido).
    const s = io(window.location.origin, {
      path: "/api/socket.io/",
      withCredentials: true,
    });
    setSocket(s);
    return () => { s.disconnect(); };
  }, []);

  const contaAtiva = contas.find((c) => c.status === "ativo") || contas[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center sm:ml-16">
        <Loader2 className="w-6 h-6 animate-spin text-ink-4" />
      </div>
    );
  }

  if (!contaAtiva) {
    return (
      <div className="min-h-screen bg-base flex flex-col items-center justify-center text-ink-2 p-6 sm:ml-16">
        <MessageCircle className="w-12 h-12 text-ink-5 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Nenhuma conta WhatsApp conectada</h2>
        <p className="text-sm text-ink-3 max-w-md text-center mb-6">
          Para usar o chat, conecte primeiro uma conta Meta WhatsApp Cloud API
          em <strong>WhatsApp → Oficial (Meta)</strong>.
        </p>
        <Link
          to="/whatsapp/oficial"
          className="px-4 py-2 bg-brand-600 hover:bg-brand-500 rounded text-white text-sm transition"
        >
          Conectar conta
        </Link>
      </div>
    );
  }

  return (
    <div className="h-screen bg-base flex flex-col sm:ml-16">
      <header className="px-6 py-3 border-b border-white/[0.06] flex items-center gap-3 flex-shrink-0">
        <MessageCircle className="w-5 h-5 text-brand-400" />
        <h1 className="text-lg font-semibold text-white">Chat</h1>
        <span className="text-xs text-ink-4 ml-2 truncate">
          {contaAtiva.display_name || contaAtiva.phone_number || `Conta #${contaAtiva.id}`}
        </span>
      </header>
      <div className="flex-1 min-h-0">
        <InboxLayout
          contaId={contaAtiva.id}
          socket={socket}
          wrapperClass="flex h-full bg-base overflow-hidden"
        />
      </div>
    </div>
  );
}
